import React, { useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { Modal, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { db } from '../../../../firebase';
import { Customer } from '../../customers/CustomerInterfaces';
import { CONTACT_TYPE_BADGE_CLASS, CONTACT_TYPES } from '../../../constants/contactTypes';

type BadgeKey = keyof typeof CONTACT_TYPE_BADGE_CLASS;
const badgeClassFor = (type: string | undefined): string =>
  (type && CONTACT_TYPE_BADGE_CLASS[type as BadgeKey]) || 'badge-light-secondary';

interface ContactRevenueData {
  total: number;
  outstanding: number;
  paid: number;
}

interface ContactJobCounts {
  active: number;
  completed: number;
  total: number;
}

interface ContactsListProps {
  clients: Customer[];
  onSelectClient: (contact: Customer) => void;
  getClientRevenue: (customerId: string) => ContactRevenueData;
  getClientJobCounts: (customerId: string) => ContactJobCounts;
  /** contactId → active-recurring-template count (subscription lives in the
   *  page so the same data drives the row badge AND the "Recurring" sort). */
  recurringByContact: Record<string, number>;
  loading: boolean;
}

export function ClientsList({
  clients,
  onSelectClient,
  getClientRevenue,
  getClientJobCounts,
  recurringByContact,
  loading,
}: ContactsListProps) {
  // Inline contact-type editor — click a Type pill → pick a new type → confirm →
  // direct Firestore write (no Andrei; the customers snapshot refreshes the row).
  // The modal is the dropdown + the confirmation in one (no table-overflow clip).
  const [editing, setEditing] = useState<Customer | null>(null);
  const [selectedType, setSelectedType] = useState<string>('');
  const [savingType, setSavingType] = useState(false);

  const openTypeEditor = (contact: Customer) => {
    setEditing(contact);
    setSelectedType(contact.contactType || 'Customer');
  };
  const confirmTypeChange = async () => {
    if (!editing?.id || !selectedType || selectedType === editing.contactType) { setEditing(null); return; }
    setSavingType(true);
    try {
      await updateDoc(doc(db, 'customers', editing.id), { contactType: selectedType, lastEdited: new Date().toISOString() });
      toast.success(`${editing.company || editing.name || 'Contact'} → ${selectedType}`);
      setEditing(null);
    } catch (err: any) {
      console.error('[ClientsList] contact-type update failed:', err);
      toast.error(err?.message || 'Could not change contact type');
    } finally {
      setSavingType(false);
    }
  };

  const formatCurrency = (amount: number) => {
    if (amount === 0) return '-';
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (value: any) => {
    if (!value) return '-';
    
    let date: Date;
    
    // Handle Firestore Timestamp objects (have seconds and nanoseconds)
    if (value && typeof value === 'object' && 'seconds' in value) {
      date = new Date(value.seconds * 1000);
    }
    // Handle Date objects
    else if (value instanceof Date) {
      date = value;
    }
    // Handle string dates
    else if (typeof value === 'string') {
      // Handle Xero date format: /Date(1234567890000+0000)/
      const xeroDateMatch = value.match(/\/Date\((\d+)(?:[+-]\d+)?\)\//);
      if (xeroDateMatch) {
        date = new Date(parseInt(xeroDateMatch[1], 10));
      } else {
        date = new Date(value);
      }
    }
    // Handle numeric timestamps
    else if (typeof value === 'number') {
      // If it looks like seconds (< 1e12), convert to ms
      const ms = value < 1e12 ? value * 1000 : value;
      date = new Date(ms);
    }
    else {
      return '-';
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) return '-';
    
    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
              <thead>
                <tr className="fw-bold text-muted">
                  <th className="min-w-200px">Contact</th>
                  <th className="min-w-100px">Type</th>
                  <th className="min-w-100px">Jobs</th>
                  <th className="min-w-120px">Revenue</th>
                  <th className="min-w-100px">Outstanding</th>
                  <th className="min-w-120px">Last Contact</th>
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4, 5].map((i) => (
                  <tr key={i}>
                    <td>
                      <div className="placeholder-glow">
                        <span className="placeholder col-8"></span>
                        <span className="placeholder col-6"></span>
                      </div>
                    </td>
                    <td><span className="placeholder col-6"></span></td>
                    <td><span className="placeholder col-4"></span></td>
                    <td><span className="placeholder col-6"></span></td>
                    <td><span className="placeholder col-6"></span></td>
                    <td><span className="placeholder col-8"></span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  if (clients.length === 0) {
    return (
      <div className="card">
        <div className="card-body py-10 text-center">
          <i className="bi bi-people fs-1 text-muted"></i>
          <div className="fw-bold fs-4 mt-4">No contacts found</div>
          <div className="text-muted mt-2">
            Try adjusting your filters or add a new contact.
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="card">
      <div className="card-body py-3">
        <div className="table-responsive">
          <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4 table-hover">
            <thead>
              <tr className="fw-bold text-muted">
                <th className="min-w-200px">Contact</th>
                <th className="min-w-100px">Type</th>
                <th className="min-w-100px text-center d-none d-md-table-cell">Jobs</th>
                <th className="min-w-120px text-end">Revenue</th>
                <th className="min-w-100px text-end d-none d-md-table-cell">Outstanding</th>
                <th className="min-w-120px d-none d-md-table-cell">Last Contact</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((contact) => {
                const revenue = getClientRevenue(contact.id || '');
                const jobCounts = getClientJobCounts(contact.id || '');
                const recurringCount = recurringByContact[contact.id || ''] || 0;

                return (
                  <tr 
                    key={contact.id} 
                    className="cursor-pointer"
                    onClick={() => onSelectClient(contact)}
                  >
                    {/* Contact Info */}
                    <td>
                      <div className="d-flex align-items-center">
                        <div className="symbol symbol-45px me-3">
                          <span className="symbol-label bg-light-primary text-primary fw-bold fs-5">
                            {(contact.company || contact.name || '?').charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="d-flex flex-column">
                          <span className="text-gray-800 fw-bold text-hover-primary d-inline-flex align-items-center gap-2">
                            {contact.company || contact.name}
                            {/* BLUE = invoiced on a schedule (we issue it).
                                Green is reserved for money that arrives by
                                itself (a Stripe card-on-file arrangement), so
                                using it here said the opposite of the truth. */}
                            {recurringCount > 0 && (
                              <span
                                className="badge badge-light-primary d-inline-flex align-items-center gap-1 fs-9 fw-semibold"
                                title={`${recurringCount} scheduled invoice template${recurringCount === 1 ? '' : 's'} — we issue it, they pay however they like`}
                              >
                                <i className="bi bi-arrow-repeat" />
                                {recurringCount}
                              </span>
                            )}
                          </span>
                          {contact.company && contact.name && (
                            <span className="text-muted fs-7">{contact.name}</span>
                          )}
                          {contact.email && (
                            <span className="text-muted fs-8">{contact.email}</span>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Type — click to change (dropdown + confirm in a modal) */}
                    <td onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className={`badge border-0 d-inline-flex align-items-center gap-1 ${badgeClassFor(contact.contactType)}`}
                        style={{ cursor: 'pointer' }}
                        onClick={(e) => { e.stopPropagation(); openTypeEditor(contact); }}
                        title="Change contact type"
                      >
                        {contact.contactType || 'Unknown'}
                        <i className="bi bi-chevron-down fs-9 opacity-75"></i>
                      </button>
                    </td>

                    {/* Jobs */}
                    <td className="text-center d-none d-md-table-cell">
                      {jobCounts.total > 0 ? (
                        <div className="d-flex flex-column align-items-center">
                          <span className="fw-bold text-gray-800">{jobCounts.total}</span>
                          {jobCounts.active > 0 && (
                            <span className="badge badge-light-warning fs-9">{jobCounts.active} active</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>

                    {/* Revenue */}
                    <td className="text-end">
                      <span className={`fw-bold ${revenue.total > 0 ? 'text-success' : 'text-muted'}`}>
                        {formatCurrency(revenue.total)}
                      </span>
                    </td>

                    {/* Outstanding */}
                    <td className="text-end d-none d-md-table-cell">
                      <span className={`fw-bold ${revenue.outstanding > 0 ? 'text-warning' : 'text-muted'}`}>
                        {formatCurrency(revenue.outstanding)}
                      </span>
                    </td>

                    {/* Last Contact */}
                    <td className="d-none d-md-table-cell">
                      <span className="text-muted fs-7">
                        {formatDate(contact.lastContacted)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>

    {/* Contact-type change — picker + confirmation */}
    <Modal show={!!editing} onHide={() => !savingType && setEditing(null)} centered>
      <Modal.Header closeButton={!savingType}>
        <Modal.Title className="fs-5 fw-bold">Change contact type</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {editing && (
          <>
            <div className="mb-4">
              <span className="fw-semibold">{editing.company || editing.name || 'Contact'}</span>
              <span className="text-muted ms-1">is currently</span>
              <span className={`badge ms-2 ${badgeClassFor(editing.contactType)}`}>
                {editing.contactType || 'Unknown'}
              </span>
            </div>
            <label className="form-label fs-7 text-muted">New type</label>
            <select
              className="form-select"
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              disabled={savingType}
            >
              {CONTACT_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            {selectedType !== editing.contactType && (
              <div className="mt-3 text-muted fs-8">
                This re-classifies how they’re filtered, sorted and surfaced across the app.
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={() => setEditing(null)} disabled={savingType}>Cancel</Button>
        <Button
          variant="primary"
          onClick={confirmTypeChange}
          disabled={savingType || !editing || selectedType === editing?.contactType}
        >
          {savingType ? 'Saving…' : 'Confirm change'}
        </Button>
      </Modal.Footer>
    </Modal>
    </>
  );
}

export default ClientsList;
